import { useEffect } from 'react';
import { Form, Modal, Typography } from 'antd';
import { ProFormTextArea } from '@ant-design/pro-components';
import type { SystemConfig } from './api';

interface ConfigResetModalProps {
  config: SystemConfig | null;
  submitting: boolean;
  formatValue: (config: SystemConfig, value: number) => string;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}

/**
 * Trả về mặc định cũng phải kèm lý do như lúc đổi (BR-802).
 *
 * Tách thành modal riêng thay vì `Modal.confirm` có ô nhập tự chế: `confirm` không
 * quản lý được trạng thái form, nên phải giữ giá trị trong biến ngoài và tự kiểm
 * bằng tay — đúng chỗ dễ để lọt một lần trả về mặc định không có lý do.
 */
export function ConfigResetModal({
  config,
  submitting,
  formatValue,
  onSubmit,
  onCancel,
}: ConfigResetModalProps) {
  const [form] = Form.useForm<{ reason: string }>();

  useEffect(() => {
    if (config) form.setFieldsValue({ reason: '' });
  }, [config, form]);

  return (
    <Modal
      open={config !== null}
      title={config ? `Trả ${config.key} về mặc định?` : ''}
      okText="Trả về mặc định"
      cancelText="Hủy"
      confirmLoading={submitting}
      onCancel={onCancel}
      destroyOnHidden
      onOk={() => {
        form
          .validateFields()
          .then((values) => onSubmit(values.reason.trim()))
          .catch(() => {});
      }}
    >
      {config && (
        <>
          <Typography.Paragraph>
            Sẽ quay lại {formatValue(config, config.defaultValue)}. Có tác dụng ngay.
          </Typography.Paragraph>

          <Form form={form} layout="vertical" requiredMark={false}>
            <ProFormTextArea
              name="reason"
              label="Lý do thay đổi"
              placeholder=""
              rules={[
                { required: true, message: 'Chưa nhập lý do thay đổi.' },
                { max: 500, message: 'Lý do quá dài.' },
              ]}
              fieldProps={{ rows: 2 }}
            />
          </Form>
        </>
      )}
    </Modal>
  );
}
